
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface CreatePACTModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreatePACT: (name: string, duration: number, maxSkips: number) => void;
}

const CreatePACTModal = ({ isOpen, onClose, onCreatePACT }: CreatePACTModalProps) => {
  const [name, setName] = useState('');
  const [duration, setDuration] = useState('21');
  const [skips, setSkips] = useState('3');

  const suggestedEmojis = ['🎯', '💪', '🌟', '🔥', '✨', '🚀', '💎', '⭐'];
  
  const suggestedPacks = [
    { name: 'Read 10 pages', emoji: '📖' },
    { name: 'Meditate 5 minutes', emoji: '🧘‍♂️' },
    { name: 'Eat healthy', emoji: '🥗' },
    { name: 'Journaling', emoji: '📓' },
    { name: 'Daily planning', emoji: '📅' },
    { name: 'Weekly planning', emoji: '📆' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const durationNumber = parseInt(duration) || 1;
    const maxSkips = parseInt(skips) || 3;
    if (name.trim() && durationNumber > 0) {
      onCreatePACT(name.trim(), durationNumber, maxSkips);
      setName('');
      setDuration('21');
      setSkips('3');
      onClose();
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 365)) {
      setDuration(value);
    }
  };

  const handleEmojiClick = (emoji: string) => {
    setName(prev => prev + emoji);
  };

  const handleSuggestedPackClick = (pack: { name: string; emoji: string }) => {
    setName(`${pack.name} ${pack.emoji}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md mx-auto bg-white rounded-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-deep-blue text-center">
            Create Your PACT
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Suggested Packs */}
          <div>
            <Label className="text-deep-blue font-medium mb-3 block">
              Quick Pacts Selections
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {suggestedPacks.map((pack, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSuggestedPackClick(pack)}
                  className="flex items-center gap-2 p-3 bg-soft-blue hover:bg-vibrant-orange/10 rounded-lg transition-colors text-left"
                >
                  <span className="text-lg">{pack.emoji}</span>
                  <span className="text-sm text-deep-blue">{pack.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="name" className="text-deep-blue font-medium">
              What will you show up for?
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="e.g., Read 10 pages, Meditate 5 minutes"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 border-soft-blue focus:border-vibrant-orange rounded-lg"
              required
            />
            
            {/* Emoji Suggestions */}
            <div className="mt-2">
              <Label className="text-sm text-deep-blue/70 mb-2 block">
                Add an emoji:
              </Label>
              <div className="flex gap-2 flex-wrap">
                {suggestedEmojis.map((emoji, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleEmojiClick(emoji)}
                    className="w-8 h-8 text-lg hover:bg-soft-blue rounded transition-colors"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div>
            <Label htmlFor="duration" className="text-deep-blue font-medium">
              For how many days?
            </Label>
            <Input
              id="duration"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              min="1"
              max="365"
              value={duration}
              onChange={handleDurationChange}
              className="mt-2 border-soft-blue focus:border-vibrant-orange rounded-lg"
              required
            />
            <p className="text-sm text-deep-blue/60 mt-1">
              21 days is perfect for building a habit
            </p>
          </div>

          {/* Skip Selection */}
          <div>
            <Label className="text-deep-blue font-medium mb-3 block">
              How many skips do you want? (Maximum 3)
            </Label>
            <RadioGroup value={skips} onValueChange={setSkips} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="skip1" />
                <Label htmlFor="skip1" className="text-deep-blue">1 skip</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="2" id="skip2" />
                <Label htmlFor="skip2" className="text-deep-blue">2 skips</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="3" id="skip3" />
                <Label htmlFor="skip3" className="text-deep-blue">3 skips</Label>
              </div>
            </RadioGroup>
            <p className="text-sm text-deep-blue/60 mt-1">
              Skips let you miss a day without breaking your streak
            </p>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-soft-blue text-deep-blue hover:bg-soft-blue rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-lg"
            >
              Create PACT
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePACTModal;
